import { useCallback, useEffect, useState } from "react";
import { FormProvider, get, useForm } from "react-hook-form";

import type { z } from "zod";
import { produce } from "immer";
import debounce from "lodash.debounce";
import { AnimatePresence, motion } from "framer-motion";

import { t } from "@lingui/macro";
import { X } from "@phosphor-icons/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useResumeStore } from "@/client/stores/resume";
import { AiActions } from "@/client/components/ai-actions";
import { T } from "@lingui/react/dist/shared/react.e5f95de8";
import { useDialog, DialogName } from "@/client/stores/dialog";
import { useSaveStatusStore } from "@/client/stores/useSaveStatusStore";

import {
  Badge,
  BadgeInput,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
} from "@reactive-resume/ui";
import {
  defaultCustomSection,
  customSectionSchema,
  SectionWithItem,
} from "@reactive-resume/schema";

import { useAutosaveStatus } from "../../../_components/SavingIndicator";

import { URLInput } from "../sections/shared/url-input";

const formSchema = customSectionSchema;

type FormValues = z.infer<typeof formSchema>;

export const CustomSectionForm = ({ id }: { id: string }) => {
  const { payload } = useDialog<T>(id as DialogName);

  const form = useForm<FormValues>({
    defaultValues: { ...defaultCustomSection, ...payload?.item },
    resolver: zodResolver(formSchema),
  });

  const setValue = useResumeStore((state) => state.setValue);

  const section = useResumeStore((state) => {
    return get(state.resume.data.sections, id);
  }) as SectionWithItem<T> | null;

  const [pendingKeyword, setPendingKeyword] = useState("");

  const onSubmit = (values: T) => {
    if (!section) return;

    if (!payload.item?.id) return;

    // if (pendingKeyword && "keywords" in values) {
    //   values?.keywords.push(pendingKeyword);
    // }

    setValue(
      `sections.${id}.items`,
      produce(section.items, (draft: T[]): void => {
        const index = draft.findIndex((item) => item.id === payload.item?.id);
        if (index === -1) return;
        draft[index] = values;
      }),
    );
  };

  const { status, triggerSave } = useAutosaveStatus({
    saveFn: async () => {
      const values = form.getValues();
      onSubmit(values);
    },
    debounceMs: 500,
    minSavingMs: 300,
    savedVisibleMs: 1200,
  });

  const setStatus = useSaveStatusStore((s) => s.setStatus);

  useEffect(() => {
    setStatus(status);
  }, [status, setStatus]);

  const debouncedSubmit = useCallback(
    debounce(() => {
      triggerSave(); // notify status hook
    }, 500),
    [form, triggerSave],
  );

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!payload) return null;

  return (
    <FormProvider {...form}>
      <form className="max-h-[60vh] space-y-6 lg:max-h-fit" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            name="name"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t`Name`}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-white"
                    onChange={(e) => {
                      field.onChange(e);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="description"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t`Description`}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-white"
                    onChange={(e) => {
                      field.onChange(e);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="date"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t`Date or Date Range`}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-white"
                    onChange={(e) => {
                      field.onChange(e);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="location"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t`Location`}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-white"
                    onChange={(e) => {
                      field.onChange(e);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="url"
            control={form.control}
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>{t`Website`}</FormLabel>
                <FormControl>
                  <URLInput
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="summary"
            control={form.control}
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>{t`Summary`}</FormLabel>
                <FormControl>
                  <RichInput
                    {...field}
                    content={field.value}
                    footer={(editor) => (
                      <AiActions
                        value={editor.getText()}
                        onChange={(value) => {
                          editor.commands.setContent(value, true);
                          field.onChange(value);
                          debouncedSubmit();
                        }}
                      />
                    )}
                    className="bg-white"
                    onChange={(value) => {
                      field.onChange(value);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="keywords"
            control={form.control}
            render={({ field }) => (
              <div className="space-y-3 sm:col-span-2">
                <FormItem>
                  <FormLabel>{t`Keywords`}</FormLabel>
                  <FormControl>
                    <BadgeInput
                      {...field}
                      setPendingKeyword={setPendingKeyword}
                      className="bg-white"
                      onChange={(e) => {
                        field.onChange(e);
                        debouncedSubmit();
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    {t`You can add multiple keywords by separating them with a comma or pressing enter.`}
                  </FormDescription>
                  <FormMessage />
                </FormItem>

                <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
                  <AnimatePresence>
                    {field.value.map((item, index) => (
                      <motion.div
                        key={item}
                        layout
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0, transition: { delay: index * 0.1 } }}
                        exit={{ opacity: 0, x: -50 }}
                      >
                        <Badge
                          className="cursor-pointer"
                          onClick={() => {
                            const newKeywords = field.value.filter((v) => item !== v);
                            field.onChange(newKeywords);
                            debouncedSubmit();
                          }}
                        >
                          <span className="mr-1">{item}</span>
                          <X size={12} weight="bold" />
                        </Badge>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}
          />
        </div>
      </form>
    </FormProvider>
  );
};
